import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { formatGBP } from "@/lib/utils-cart";
import { Search, SlidersHorizontal } from "lucide-react";

export default function Marketplace() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [loading, setLoading] = useState(true);

  const activeCat = params.get("category") || "";

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = new URLSearchParams();
    if (params.get("q")) p.set("q", params.get("q"));
    if (params.get("category")) p.set("category", params.get("category"));
    p.set("limit", "60");
    api.get(`/products?${p.toString()}`).then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, [params]);

  const submitSearch = (e) => {
    e.preventDefault();
    const np = new URLSearchParams(params);
    if (q) np.set("q", q); else np.delete("q");
    setParams(np);
  };

  const setCategory = (slug) => {
    const np = new URLSearchParams(params);
    if (slug) np.set("category", slug); else np.delete("category");
    setParams(np);
  };

  return (
    <PublicLayout>
      <section className="border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="label-eyebrow text-secondary mb-2">Marketplace</div>
          <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-primary leading-none">Source from vetted UK suppliers.</h1>
          <form onSubmit={submitSearch} className="mt-8 flex gap-2 max-w-2xl">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search products, suppliers, categories…" className="v-input pl-10" data-testid="marketplace-search" />
            </div>
            <button className="v-btn-primary" data-testid="marketplace-search-submit">Search</button>
          </form>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-10 grid lg:grid-cols-[240px_1fr] gap-10">
        <aside>
          <div className="label-eyebrow text-foreground mb-3">Categories</div>
          <div className="flex flex-col gap-1">
            <button onClick={() => setCategory("")} data-testid="cat-all" className={`text-left text-sm py-2 px-3 rounded-sm border transition-colors ${!activeCat ? "border-primary bg-primary text-primary-foreground" : "border-transparent hover:bg-muted"}`}>All categories</button>
            {categories.map((c) => (
              <button key={c.id} onClick={() => setCategory(c.slug)} data-testid={`cat-${c.slug}`} className={`text-left text-sm py-2 px-3 rounded-sm border transition-colors ${activeCat === c.slug ? "border-primary bg-primary text-primary-foreground" : "border-transparent hover:bg-muted"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </aside>
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-muted-foreground">{loading ? "Loading…" : `${products.length} products`}</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {products.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group v-card p-3 flex flex-col" data-testid={`product-card-${p.id}`}>
                <div className="aspect-square overflow-hidden bg-muted">
                  <img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                </div>
                <div className="pt-3 flex-1 flex flex-col">
                  <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{p.category}</div>
                  <h3 className="text-sm font-semibold text-primary mt-1 leading-snug line-clamp-2">{p.title}</h3>
                  <div className="mt-auto pt-2 flex items-baseline gap-2">
                    <span className="font-display font-bold text-lg text-foreground">{formatGBP(p.price)}</span>
                    {p.compare_at_price && p.compare_at_price > p.price && (
                      <span className="text-xs text-muted-foreground line-through">{formatGBP(p.compare_at_price)}</span>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {!loading && products.length === 0 && (
            <div className="text-center py-20 text-muted-foreground">
              <SlidersHorizontal size={32} className="mx-auto mb-3" />
              No products match your filters.
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
